import { ChangeDetectionStrategy, Component, effect, inject, model, signal } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { ModalComponent } from "../modal/modal.component";
import { ModalHeaderComponent } from "../modal/modal.header.component";
import { ModalData } from "../modal/modal.service";
import { SearchStore } from "../../stores/search.store";
import { SearchService } from "../../services/search/search.service";
import { S25Util } from "../../util/s25-util";

export interface ModalSaveSearchParams {
    // TODO: use a return value instead of passing a callback
    onSaved: (queryId: number) => void;
    initialName: string;
    asNew: boolean;
    initialFav: boolean;
}

@Component({
    selector: "save-search-modal",
    template: `
        <div class="s25-ng">
            <s25-modal-header [data]="headerData()"></s25-modal-header>
            <div class="modal-body">
                <div class="">
                    <label class="ngBold ngBlock">Search Name</label>
                    <input
                        type="text"
                        class="c-input"
                        [ngModel]="name()"
                        (ngModelChange)="name.set($event)"
                        [placeholder]="'Name your search'"
                        [maxLength]="40"
                    />
                </div>
                <div class="c-margin-top--single">
                    <label class="ngBold ngBlock">Add to starred searches</label>
                    <div role="radioGroup">
                        <label class="ngBlock">
                            <input type="radio" [name]="favName" [checked]="!fav()" (change)="fav.set(false)" />
                            No
                        </label>
                        <label class="ngBlock">
                            <input type="radio" [name]="favName" [checked]="fav()" (change)="fav.set(true)" />
                            Yes
                        </label>
                    </div>
                </div>

                <div class="flex end c-margin-top--single">
                    @if (error()) {
                        <span class="ngBold ngRed">{{ error() }}</span>
                    }
                    <div class="buttons">
                        @if (isLoading()) {
                            <span class="aw-button aw-button--primary" aria-busy="true">Saving...</span>
                        }
                        <button class="aw-button aw-button--primary" [disabled]="isLoading()" (click)="onSave()">
                            Save
                        </button>
                        <button class="aw-button aw-button--outline" [disabled]="isLoading()" (click)="close()">
                            Cancel
                        </button>
                    </div>
                </div>
            </div>
        </div>
    `,
    styles: `
        .buttons {
            display: flex;
            gap: 0.5em;
            justify-content: flex-end;
            margin-left: auto;
        }
    `,
    standalone: true,
    imports: [ModalHeaderComponent, FormsModule],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SaveSearchModalComponent extends ModalComponent<ModalSaveSearchParams> {
    static size = "md" as const;

    private readonly store = inject(SearchStore);

    readonly name = model("");
    readonly fav = model(false);
    readonly error = signal("");
    readonly isLoading = signal(false);

    readonly favName = "searchAddToFav";

    constructor() {
        super();
        effect(() => {
            const p = this.params();
            if (!p) return;
            this.name.set(p.initialName);
            this.fav.set(p.initialFav);
            this.error.set("");
            this.isLoading.set(false);
        });
    }

    readonly headerData = (): ModalData => ({
        ...this.data,
        title: "Save Search",
    });

    close() {
        this.data.closeModal();
    }

    async onSave() {
        const nameVal = this.name().trim();
        if (!nameVal) {
            this.error.set("Please Enter a valid search name");
            return;
        }
        this.error.set("");
        this.isLoading.set(true);

        const modelPromise = this.store.complexSearch();
        let searchDataPromise: Promise<
            { model?: unknown; searches?: Record<string, { query_id: number }> } | undefined
        > = Promise.resolve(undefined);
        if (!this.params()?.asNew) {
            const fromSaved = this.store.fromSavedSearch();
            if (fromSaved?.itemId) {
                searchDataPromise = SearchService.getFullSearchCriteria(this.store.typeId(), Number(fromSaved.itemId));
            }
        }

        const [model, searchData] = await Promise.all([modelPromise, searchDataPromise]);
        const originalSearch = searchData?.model;

        const [newSearch, error] = await S25Util.Maybe(
            SearchService.createSearch({
                model: S25Util.deepCopy(model.model),
                searches: S25Util.deepCopy(model.searches || {}),
                typeId: this.store.typeId(),
                itemIdArr: undefined,
                isNewSearch: this.params()?.asNew ?? true,
                rootName: nameVal,
                makePub: null,
                makeFav: this.fav(),
                origModel: originalSearch as SearchService.SearchModel,
            }),
        );

        this.isLoading.set(false);
        if (error) {
            this.error.set("Something went wrong.");
            return;
        }

        // Creating the new search already re-created all the nested searches,
        // so let's clean up the old ones!
        for (const tempSearch of Object.values(searchData?.searches || {}) as { query_id: number }[]) {
            void SearchService.deleteSearch(tempSearch.query_id, this.store.typeId());
        }

        this.params()?.onSaved(newSearch.queryId);
        this.close();
    }
}
